/* =========================================================================
   Breizh & Dragons — app.js
   JS vanilla, zéro dépendance, chargé en `defer`.
   Amélioration progressive : chaque module fait du feature-detection
   et reste no-op si ses éléments ne sont pas présents sur la page.
   Respecte prefers-reduced-motion.
   ========================================================================= */
(function () {
  'use strict';

  /* L'utilisateur préfère-t-il moins d'animations ? */
  var prefersReducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  /* Sélecteurs des éléments focusables (pour le piège à focus du menu). */
  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* ---------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* ---------------------------------------------------------------------
     1. Menu mobile (overlay / drawer)
     Hooks attendus dans le header :
       [data-menu-toggle]  → bouton ☰ qui ouvre le menu
       [data-menu]         → le panneau overlay/drawer
       [data-menu-close]   → bouton(s) de fermeture (croix)
       [data-menu-backdrop]→ voile cliquable (optionnel)
     Ouvre/ferme, gère Escape, le focus et le verrouillage du scroll body.
     --------------------------------------------------------------------- */
  function initMobileMenu() {
    var toggle = qs('[data-menu-toggle]');
    var menu = qs('[data-menu]');
    if (!toggle || !menu) { return; } // no-op si absent

    var closers = qsa('[data-menu-close]');
    var backdrop = qs('[data-menu-backdrop]');
    var lastFocused = null;

    function openMenu() {
      lastFocused = document.activeElement;
      menu.classList.add('is-open');
      menu.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('menu-open'); // verrou scroll (CSS)
      document.addEventListener('keydown', onKeydown);
      // Focus le premier élément interactif du menu.
      var first = qs(FOCUSABLE, menu);
      if (first) { first.focus(); }
    }

    function closeMenu() {
      menu.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('menu-open');
      document.removeEventListener('keydown', onKeydown);
      // Cache le panneau après la transition (sauf reduced-motion → tout de suite).
      var hide = function () { menu.setAttribute('hidden', ''); };
      if (prefersReducedMotion.matches) {
        hide();
      } else {
        window.setTimeout(hide, 300);
      }
      // Rends le focus au déclencheur.
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      } else {
        toggle.focus();
      }
    }

    function isOpen() { return menu.classList.contains('is-open'); }

    /* Escape ferme + piège à focus léger (Tab cycle dans le menu). */
    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        closeMenu();
        return;
      }
      if (e.key !== 'Tab') { return; }
      var focusables = qsa(FOCUSABLE, menu).filter(function (el) {
        return el.offsetParent !== null; // visible uniquement
      });
      if (!focusables.length) { return; }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* État initial : fermé. */
    toggle.setAttribute('aria-expanded', 'false');
    if (!menu.hasAttribute('hidden') && !isOpen()) {
      menu.setAttribute('hidden', '');
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      isOpen() ? closeMenu() : openMenu();
    });
    closers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        closeMenu();
      });
    });
    if (backdrop) {
      backdrop.addEventListener('click', closeMenu);
    }
  }

  /* ---------------------------------------------------------------------
     1bis. Méga-menu desktop ("La Bibliothèque")
     Hooks dans le header :
       [data-mega]        → le <li> conteneur
       [data-mega-toggle] → le <button> qui ouvre
       [data-mega-panel]  → le panneau pleine largeur
     Survol (desktop) + clic (clavier/tactile), Escape, clic extérieur.
     --------------------------------------------------------------------- */
  function initMegaMenu() {
    var items = qsa('[data-mega]');
    if (!items.length) { return; } // no-op si absent

    var desktop = window.matchMedia('(min-width: 901px)');

    items.forEach(function (item) {
      var toggle = qs('[data-mega-toggle]', item);
      var panel = qs('[data-mega-panel]', item);
      if (!toggle || !panel) { return; }

      var hideTimer = null;

      function isOpen() { return panel.classList.contains('is-open'); }

      function open() {
        window.clearTimeout(hideTimer);
        panel.removeAttribute('hidden');
        void panel.offsetWidth; // reflow : la transition part de l'état fermé
        panel.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }

      function close() {
        if (!isOpen()) { return; }
        panel.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        var hide = function () {
          if (!isOpen()) { panel.setAttribute('hidden', ''); }
        };
        if (prefersReducedMotion.matches) {
          hide();
        } else {
          hideTimer = window.setTimeout(hide, 220);
        }
      }

      /* Clic : clavier, tactile, et souris sans survol. */
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        isOpen() ? close() : open();
      });

      /* Survol : desktop uniquement (en mobile le drawer prend le relais). */
      item.addEventListener('mouseenter', function () {
        if (desktop.matches) { open(); }
      });
      item.addEventListener('mouseleave', function () {
        if (desktop.matches) { close(); }
      });

      /* Le focus quitte le méga-menu → on referme. */
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) { close(); }
      });

      document.addEventListener('keydown', function (e) {
        if ((e.key === 'Escape' || e.key === 'Esc') && isOpen()) {
          close();
          toggle.focus();
        }
      });

      document.addEventListener('click', function (e) {
        if (isOpen() && !item.contains(e.target)) { close(); }
      });
    });
  }

  /* ---------------------------------------------------------------------
     2. Galerie PDP
     Hooks :
       [data-gallery-main]  → <img> de l'image principale
       [data-gallery-thumb] → miniatures cliquables (data-image="url" + alt)
     Clic miniature → remplace l'image principale + marque l'active.
     --------------------------------------------------------------------- */
  function initGallery() {
    var main = qs('[data-gallery-main]');
    var thumbs = qsa('[data-gallery-thumb]');
    if (!main || !thumbs.length) { return; } // no-op si absent

    function activate(thumb) {
      // Récupère la source haute déf (data-image) ou celle de l'aperçu.
      var src = thumb.getAttribute('data-image');
      var thumbImg = thumb.tagName === 'IMG' ? thumb : qs('img', thumb);
      if (!src && thumbImg) {
        src = thumbImg.getAttribute('src');
      }
      if (!src) { return; }

      main.setAttribute('src', src);
      // Garde le srcset cohérent si une version responsive est fournie.
      var srcset = thumb.getAttribute('data-srcset');
      if (srcset) { main.setAttribute('srcset', srcset); }
      // Alt fidèle à la vue choisie.
      var alt = thumb.getAttribute('data-alt') ||
        (thumbImg && thumbImg.getAttribute('alt'));
      if (alt) { main.setAttribute('alt', alt); }

      // Marque la miniature active (style + a11y).
      thumbs.forEach(function (t) {
        t.classList.remove('is-active');
        t.setAttribute('aria-current', 'false');
      });
      thumb.classList.add('is-active');
      thumb.setAttribute('aria-current', 'true');
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();
        activate(thumb);
      });
    });
  }

  /* ---------------------------------------------------------------------
     3. Sticky add-to-cart mobile
     Hooks :
       [data-product-form] → le form ATC principal (sentinelle)
       [data-sticky-atc]   → la barre fixe en bas d'écran
     IntersectionObserver : quand le form sort de l'écran (vers le haut),
     on affiche la barre ; tant qu'il est visible, on la masque.
     --------------------------------------------------------------------- */
  function initStickyAtc() {
    var form = qs('[data-product-form]');
    var sticky = qs('[data-sticky-atc]');
    if (!form || !sticky) { return; } // no-op si absent

    // Fallback : pas d'IO → on laisse la barre toujours visible.
    if (!('IntersectionObserver' in window)) {
      sticky.classList.add('is-visible');
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Form visible → on cache la barre ; sinon on la montre.
        if (entry.isIntersecting) {
          sticky.classList.remove('is-visible');
        } else {
          sticky.classList.add('is-visible');
        }
      });
    }, { rootMargin: '0px', threshold: 0 });

    observer.observe(form);
  }

  /* ---------------------------------------------------------------------
     4. Quantité +/-
     Hooks (par groupe, scopé sur un conteneur commun ou la page) :
       [data-qty-minus] / [data-qty-plus] → boutons
       [data-qty-input]                   → <input type="number">
     Respecte min / max / step de l'input. Gère plusieurs groupes.
     --------------------------------------------------------------------- */
  function initQuantity() {
    var inputs = qsa('[data-qty-input]');
    if (!inputs.length) { return; } // no-op si absent

    inputs.forEach(function (input) {
      // Le groupe = le plus proche conteneur, sinon le document.
      var scope = input.closest('[data-qty]') || document;
      var minus = qs('[data-qty-minus]', scope);
      var plus = qs('[data-qty-plus]', scope);

      function step(dir) {
        var val = parseFloat(input.value);
        if (isNaN(val)) { val = 0; }
        var stepVal = parseFloat(input.getAttribute('step')) || 1;
        var min = input.hasAttribute('min')
          ? parseFloat(input.getAttribute('min')) : 1;
        var max = input.hasAttribute('max')
          ? parseFloat(input.getAttribute('max')) : Infinity;

        var next = val + dir * stepVal;
        if (next < min) { next = min; }
        if (next > max) { next = max; }
        input.value = next;
        // Notifie d'éventuels listeners (Shopify, total live, etc.).
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (minus) {
        minus.addEventListener('click', function (e) {
          e.preventDefault();
          step(-1);
        });
      }
      if (plus) {
        plus.addEventListener('click', function (e) {
          e.preventDefault();
          step(1);
        });
      }
    });
  }

  /* ---------------------------------------------------------------------
     5 bis. Ajout au panier sans rechargement + état « dans le panier »
     Hooks :
       form[data-cart-add] → formulaire d'ajout (carte produit ou fiche)
       [data-atc]          → le bouton ; porte data-variant-id, data-cart-url,
                             data-label-idle et data-label-done
       [data-atc-label]    → le <span> qui reçoit le libellé
       [data-cart-count]   → pastille de comptage du header
       [data-cart-live]    → région aria-live qui annonce le résultat

     Amélioration progressive : sans JS le formulaire part en POST classique
     vers /cart/add, le panier fonctionne, on perd juste le sans-rechargement.
     Une fois le produit au panier, le bouton s'inverse et mène au panier.
     --------------------------------------------------------------------- */
  function initCartAdd() {
    var forms = qsa('form[data-cart-add]');
    if (!forms.length || !window.fetch) { return; } // no-op si absent

    function announce(msg) {
      qsa('[data-cart-live]').forEach(function (node) { node.textContent = msg; });
    }

    /* Peint un bouton dans l'état demandé. */
    function paint(btn, inCart) {
      btn.classList.toggle('is-in-cart', inCart);
      var label = qs('[data-atc-label]', btn);
      var text = inCart
        ? btn.getAttribute('data-label-done')
        : btn.getAttribute('data-label-idle');
      if (label && text) { label.textContent = text; }
      if (inCart) {
        btn.setAttribute('aria-label', 'Déjà dans le panier, voir le panier');
      } else {
        btn.removeAttribute('aria-label');
      }
    }

    /* Aligne tous les boutons et le compteur sur l'état réel du panier. */
    function sync(cart) {
      var ids = (cart.items || []).map(function (item) { return String(item.variant_id); });

      qsa('[data-atc]').forEach(function (btn) {
        var id = btn.getAttribute('data-variant-id');
        if (id) { paint(btn, ids.indexOf(String(id)) !== -1); }
      });

      qsa('[data-cart-count]').forEach(function (node) {
        node.textContent = cart.item_count;
        node.classList.toggle('is-empty', cart.item_count === 0);
      });
    }

    function fetchCart() {
      return fetch(window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root + 'cart.js'
        : '/cart.js', { credentials: 'same-origin' }
      ).then(function (res) { return res.json(); });
    }

    forms.forEach(function (form) {
      var btn = qs('[data-atc]', form) || qs('button[type="submit"]', form);
      if (!btn) { return; }

      form.addEventListener('submit', function (event) {
        // Déjà au panier : le bouton devient un raccourci vers le panier.
        if (btn.classList.contains('is-in-cart')) {
          event.preventDefault();
          window.location.href = btn.getAttribute('data-cart-url') || '/cart';
          return;
        }

        event.preventDefault();
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');

        fetch(form.getAttribute('action') || '/cart/add', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        })
          .then(function (res) {
            return res.json().then(function (data) {
              // 422 : variante épuisée, quantité refusée… Shopify décrit l'erreur.
              if (!res.ok) { throw new Error(data.description || data.message || 'Ajout impossible'); }
              return data;
            });
          })
          .then(fetchCart)
          .then(function (cart) {
            // Sur la page panier elle-même, les lignes sont rendues en Liquid :
            // seule une recharge peut les faire apparaître.
            if (qs('[data-cart-reload-on-add]')) { window.location.reload(); return; }
            sync(cart);
            announce('Produit ajouté au panier. ' + cart.item_count + ' article'
              + (cart.item_count > 1 ? 's' : '') + ' au total.');
          })
          .catch(function (err) {
            // Réseau coupé ou réponse illisible : on rebascule sur le POST natif
            // plutôt que de laisser l'utilisateur sans panier.
            if (err instanceof TypeError) { form.submit(); return; }
            announce(err.message);
          })
          .then(function () {
            btn.classList.remove('is-loading');
            btn.removeAttribute('aria-busy');
          });
      });
    });

    // Réconciliation au chargement : Shopify sert parfois une page en cache
    // dont l'état du panier rendu en Liquid est périmé.
    fetchCart().then(sync).catch(function () { /* on garde l'état rendu */ });
  }

  /* ---------------------------------------------------------------------
     5 ter. Code promo du panier
     Shopify n'applique pas les codes de réduction depuis le formulaire panier :
     la route /discount/CODE les pose en session puis redirige. C'est le seul
     moyen d'afficher la remise avant le checkout.
     Hooks : [data-discount-form] → le <form>, [data-discount-input] → le champ
     --------------------------------------------------------------------- */
  function initDiscountCode() {
    var form = qs('[data-discount-form]');
    if (!form) { return; } // no-op si absent

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var input = qs('[data-discount-input]', form);
      var code = input ? input.value.trim() : '';
      if (!code) { if (input) { input.focus(); } return; }

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.location.href = root + 'discount/' + encodeURIComponent(code)
        + '?redirect=' + encodeURIComponent(root + 'cart');
    });
  }

  /* ---------------------------------------------------------------------
     5. Année courante (footer)
     Hook : [data-year] → reçoit l'année en cours.
     --------------------------------------------------------------------- */
  function initYear() {
    var nodes = qsa('[data-year]');
    if (!nodes.length) { return; } // no-op si absent
    var year = String(new Date().getFullYear());
    nodes.forEach(function (node) { node.textContent = year; });
  }

  /* ---------------------------------------------------------------------
     Init — on attend le DOM (le script est en defer, mais on sécurise).
     --------------------------------------------------------------------- */
  function init() {
    initMobileMenu();
    initMegaMenu();
    initGallery();
    initStickyAtc();
    initQuantity();
    initCartAdd();
    initDiscountCode();
    initYear();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
